// NeuroTruthDecisionPolicyProjection — governed projection of NeuroTruth strategy content
// into TYPED decision policy (doc v3.1 §5.5, issue #4004). Contract slice only: types,
// digest, fail-closed validation, and a pure resolver producing fully-populated
// ObjectiveConstraint[] for the runner's knownConstraints seam. Runtime wiring (runner /
// router / enforcement points) belongs to later lanes and NEVER lives here.
//
// Why this exists: today the live path constructs ObjectiveConstraint entries with EMPTY
// selectionPolicyEffect and the value-gap bridge runner hardcodes `knownConstraints: []`,
// so strategy constraints exist as prose but never move lever selection. This contract
// makes that inert pattern unrepresentable: a projection is approved + checksummed, its
// constraints reference the EXISTING closed ObjectiveConstraintId vocabulary with
// populated selection effects, and the validator REJECTS the empty-effect/withhold
// combination outright.
//
// Extend, never fork:
// - governance block REUSES ObjectiveConfigGovernance + stableConfigChecksum from the
//   taxonomy-overlay pattern (objective-taxonomy-resolver.ts);
// - constraint entries REUSE ObjectiveConstraintId, enforcement, and
//   selectionPolicyEffect from objective-value-bridge.ts via indexed-access types;
// - metric families / units come from the existing closed vocabularies — no new catalogs.
import {
  stableConfigChecksum,
  type ObjectiveConfigGovernance,
  type ObjectiveConfigStatus,
} from '../../objective-taxonomy-resolver';
import {
  OBJECTIVE_CONSTRAINT_IDS,
  OVERLAP_DIMENSION_KEYS_BY_METRIC_FAMILY,
  type LeverBranchId,
  type ObjectiveConstraint,
  type ObjectiveConstraintId,
  type TargetMetricFamily,
  type TargetMetricUnit,
} from './objective-value-bridge';

export const NEUROTRUTH_DECISION_POLICY_PROJECTION_SCHEMA_VERSION =
  'neurotruth_decision_policy_projection_v1' as const;

export type NeuroTruthDecisionPolicyProjectionSchemaVersion =
  typeof NEUROTRUTH_DECISION_POLICY_PROJECTION_SCHEMA_VERSION;

export const GOVERNED_POLICY_ENFORCEMENT_SOURCE_CLASS =
  'governed-policy-enforcement' as const;
export const GOVERNED_POLICY_ENFORCEMENT_RECEIPT_EVIDENCE_KIND =
  'governed_policy_enforcement_receipt_v1' as const;
export const GOVERNED_POLICY_ENFORCEMENT_RECEIPT_DESCRIPTION =
  'Approved policy enforcement receipt.' as const;
export const GOVERNED_POLICY_ENFORCEMENT_RECEIPT_PROOF_SCHEMA_VERSION =
  'governed_policy_enforcement_receipt_proof_v1' as const;

// ---------------------------------------------------------------------------
// Directives — closed kinds
// ---------------------------------------------------------------------------

// Minimal honest starter set: exactly the sections NeuroTruth strategy content carries
// TODAY (NeuroTruthPackJsonV1: summary.mission / summary.priorities / summary.constraints
// + rules[] + patterns[] + capabilities[]). Adding a kind is a contract change, not a
// prompt change — the classifier may only PICK from these.
export const NEUROTRUTH_POLICY_DIRECTIVE_KINDS = [
  'mission',
  'priority',
  'constraint',
  'rule',
  'pattern',
  'capability',
] as const;

export type NeuroTruthPolicyDirectiveKind =
  (typeof NEUROTRUTH_POLICY_DIRECTIVE_KINDS)[number];

export type NeuroTruthPolicyDirective = Readonly<{
  directiveId: string;
  kind: NeuroTruthPolicyDirectiveKind;
  // The literal strategy phrase — recorded for the audit trail, never re-parsed downstream
  // (same law as ObjectiveConstraint.sourcePhrase).
  text: string;
  // Id of the originating pack item (rule/pattern/capability id); null for summary lines.
  sourceRefId: string | null;
  evidenceRefIds: readonly string[];
}>;

// ---------------------------------------------------------------------------
// Constraints — reuse the EXISTING closed vocabulary + selection effect
// ---------------------------------------------------------------------------

// A projection-side constraint declaration. Field types are indexed off the existing
// ObjectiveConstraint contract so this can NEVER drift into a fork: enforcement and
// selectionPolicyEffect are literally the same types the bridge already consumes.
export type NeuroTruthPolicyConstraint = Readonly<{
  constraintId: ObjectiveConstraintId;
  sourcePhrase: string;
  enforcement: ObjectiveConstraint['enforcement'];
  // MUST be populated where the constraint declares effects. The validator rejects
  // enforcement 'withhold_lever_branch' with zero excluded branches — that exact
  // combination is the current production bug (inert constraints).
  selectionPolicyEffect: ObjectiveConstraint['selectionPolicyEffect'];
  narrationLabel: string;
  evidenceRefIds: readonly string[];
}>;

// ---------------------------------------------------------------------------
// Scopes — closed kinds over EXISTING vocabularies
// ---------------------------------------------------------------------------

// Scope restrictions range only over vocabularies the repo already governs: lever-branch
// ids (the bridge's LeverBranchId catalogs) and metric families (the closed
// TargetMetricFamily set). No free-text scope dimensions.
export const NEUROTRUTH_POLICY_SCOPE_KINDS = ['lever_branch', 'metric_family'] as const;

export type NeuroTruthPolicyScopeKind = (typeof NEUROTRUTH_POLICY_SCOPE_KINDS)[number];

export type NeuroTruthPolicyScopeEffect = 'include_only' | 'exclude';

export type NeuroTruthPolicyScope = Readonly<{
  scopeId: string;
  scopeKind: NeuroTruthPolicyScopeKind;
  effect: NeuroTruthPolicyScopeEffect;
  // 'lever_branch' → LeverBranchId values; 'metric_family' → TargetMetricFamily values.
  values: readonly string[];
  // Lever families the restriction applies to; empty = applies to every family.
  appliesToLeverFamilyIds: readonly string[];
  sourcePhrase: string;
  evidenceRefIds: readonly string[];
}>;

// ---------------------------------------------------------------------------
// Caps — closed kinds, each traceable to the constraint vocabulary
// ---------------------------------------------------------------------------

// Graded (numeric) forms of the closed constraint vocabulary — nothing here is invented:
//   max_headcount_reduction ← no_headcount / no_workforce_reduction
//   max_investment_value    ← no_capex
//   max_cash_outlay         ← cash_neutral
//   min_service_level       ← maintain_service_level
export const NEUROTRUTH_POLICY_CAP_KINDS = [
  'max_headcount_reduction',
  'max_investment_value',
  'max_cash_outlay',
  'min_service_level',
] as const;

export type NeuroTruthPolicyCapKind = (typeof NEUROTRUTH_POLICY_CAP_KINDS)[number];

export type NeuroTruthPolicyCap = Readonly<{
  capId: string;
  capKind: NeuroTruthPolicyCapKind;
  value: number;
  unit: TargetMetricUnit;
  // A cap on nothing is inert — validation requires at least one lever family.
  appliesToLeverFamilyIds: readonly string[];
  sourcePhrase: string;
  evidenceRefIds: readonly string[];
}>;

// ---------------------------------------------------------------------------
// The projection
// ---------------------------------------------------------------------------

export type NeuroTruthPolicyProjectionScope = Readonly<{
  tenantId: string;
  companyId: string | null;
}>;

// Versioned, approved, digestable. Effective dates live on the governance block
// (effectiveFrom/effectiveTo), exactly like the taxonomy-overlay files — no parallel
// date fields here. governance.checksum MUST equal the projection digest (verified by
// the validator), so approval covers the exact governed content.
export type NeuroTruthDecisionPolicyProjection = Readonly<{
  schemaVersion: NeuroTruthDecisionPolicyProjectionSchemaVersion;
  projectionId: string;
  version: string;
  scope: NeuroTruthPolicyProjectionScope;
  governance: ObjectiveConfigGovernance;
  directives: readonly NeuroTruthPolicyDirective[];
  constraints: readonly NeuroTruthPolicyConstraint[];
  scopes: readonly NeuroTruthPolicyScope[];
  caps: readonly NeuroTruthPolicyCap[];
  evidenceRefIds: readonly string[];
}>;

// ---------------------------------------------------------------------------
// Enforcement receipts — typed effects, no narration prose
// ---------------------------------------------------------------------------

export const NEUROTRUTH_POLICY_ENFORCEMENT_POINTS = [
  'eligibility',
  'scenario_execution',
  'package_solving',
] as const;

export type PolicyEnforcementPoint =
  (typeof NEUROTRUTH_POLICY_ENFORCEMENT_POINTS)[number];

// Typed effect summary. The WRITER narrates receipts — nothing in this union carries
// prose, and excess prose fields fail to compile against it.
export type PolicyEnforcementEffect =
  | Readonly<{
      effectKind: 'lever_branch_excluded';
      excludedLeverBranches: readonly LeverBranchId[];
    }>
  | Readonly<{
      // Family-level exclusion (asker channel, #4008 D5 closure): the asker rules out
      // specific lever FAMILIES, not a whole taxonomy branch — claiming a branch here
      // would over-claim the enforcement that actually happened.
      effectKind: 'lever_family_excluded';
      excludedLeverFamilyIds: readonly string[];
    }>
  | Readonly<{
      effectKind: 'evidence_required';
      requiredEvidenceKeys: readonly string[];
    }>
  | Readonly<{
      effectKind: 'value_capped';
      capKind: NeuroTruthPolicyCapKind;
      capValue: number;
      capUnit: TargetMetricUnit;
    }>
  | Readonly<{
      effectKind: 'scope_restricted';
      scopeKind: NeuroTruthPolicyScopeKind;
      scopeEffect: NeuroTruthPolicyScopeEffect;
      values: readonly string[];
    }>;

// Which governed entry the enforcement traces to. Three projection families plus the
// ASKER channel (#4008 D5 closure): a turn-scoped exclusion the executive stated in the
// question itself ("… excluding workforce reductions"), carried by the planner decision's
// outcomePlan.excludedCapabilityIds — approved standing policy is NOT its source, and
// attributing it to a projection entry would fabricate provenance.
export type PolicyEnforcementReceiptSource =
  | Readonly<{ kind: 'constraint'; constraintId: ObjectiveConstraintId }>
  | Readonly<{ kind: 'cap'; capId: string }>
  | Readonly<{ kind: 'scope'; scopeId: string }>
  | Readonly<{
      kind: 'asker_exclusion';
      decisionId: string;
      // The capability ids the enforcement point actually READ and mapped — never the
      // raw unvalidated list (unmapped ids must not ride a receipt as enforced).
      excludedCapabilityIds: readonly string[];
    }>;

// One receipt per exclusion / cap / scope restriction applied at enforcement time.
// projectionRef pins the EXACT approved content (id + version + digest) so a receipt can
// never be attributed to a projection revision it did not come from.
export type PolicyEnforcementReceipt = Readonly<{
  receiptId: string;
  projectionRef: Readonly<{ projectionId: string; version: string; digest: string }>;
  source: PolicyEnforcementReceiptSource;
  enforcementPoint: PolicyEnforcementPoint;
  affectedLeverFamilyIds: readonly string[];
  effect: PolicyEnforcementEffect;
  evidenceRefIds: readonly string[];
}>;

// ---------------------------------------------------------------------------
// Digest — reuse-fingerprint, stable across key order
// ---------------------------------------------------------------------------

// The digest covers the governed content of the projection (everything except the
// governance block), so a content edit invalidates governance.checksum — same shape as
// taxonomyOverlayChecksumPayload.
export function neuroTruthPolicyProjectionChecksumPayload(
  projection: Omit<NeuroTruthDecisionPolicyProjection, 'governance'> & {
    governance?: unknown;
  },
): unknown {
  return {
    schemaVersion: projection.schemaVersion,
    projectionId: projection.projectionId,
    version: projection.version,
    scope: projection.scope,
    directives: projection.directives,
    constraints: projection.constraints,
    scopes: projection.scopes,
    caps: projection.caps,
    evidenceRefIds: projection.evidenceRefIds,
  };
}

// stableConfigChecksum canonicalizes with deep-sorted keys, so the digest is stable
// across key order by construction — the reuse fingerprint for identical policy content.
export function computeNeuroTruthPolicyProjectionDigest(
  projection: Omit<NeuroTruthDecisionPolicyProjection, 'governance'> & {
    governance?: unknown;
  },
): string {
  return stableConfigChecksum(neuroTruthPolicyProjectionChecksumPayload(projection));
}

// Canonical projectionRef for enforcement receipts — the only sanctioned way to bind a
// receipt to a projection.
export function buildPolicyEnforcementReceiptProjectionRef(
  projection: NeuroTruthDecisionPolicyProjection,
): PolicyEnforcementReceipt['projectionRef'] {
  return {
    projectionId: projection.projectionId,
    version: projection.version,
    digest: computeNeuroTruthPolicyProjectionDigest(projection),
  };
}

// ---------------------------------------------------------------------------
// Asker-exclusion receipts (#4008 D5 closure) — turn-scoped, decision-pinned
// ---------------------------------------------------------------------------

// projectionRef namespace + version for receipts whose source is the ASKER, not an
// approved projection. The ref pins the EXACT planner-decision exclusion content the
// enforcement point read (id + fixed version + content digest), so an asker receipt can
// never be attributed to a turn it did not come from — the same anti-drift property
// projection receipts get from the approved-content digest.
export const ASKER_EXCLUSION_RECEIPT_PROJECTION_ID_PREFIX = 'planner_decision:';
export const ASKER_EXCLUSION_RECEIPT_VERSION = 'asker_turn_v1';

// Canonical ref builder for asker-exclusion receipts — the only sanctioned way to bind a
// receipt to a planner decision's exclusion content. stableConfigChecksum canonicalizes
// with deep-sorted keys, so the digest is stable across key order by construction.
export function buildAskerExclusionReceiptRef(
  input: Readonly<{
    decisionId: string;
    excludedCapabilityIds: readonly string[];
    excludedLeverFamilyIds: readonly string[];
  }>,
): PolicyEnforcementReceipt['projectionRef'] {
  return {
    projectionId: `${ASKER_EXCLUSION_RECEIPT_PROJECTION_ID_PREFIX}${input.decisionId}`,
    version: ASKER_EXCLUSION_RECEIPT_VERSION,
    digest: stableConfigChecksum({
      decisionId: input.decisionId,
      excludedCapabilityIds: [...input.excludedCapabilityIds],
      excludedLeverFamilyIds: [...input.excludedLeverFamilyIds],
    }),
  };
}

// ---------------------------------------------------------------------------
// Validation (fail-closed, structural). Returns an error list; empty ⇒ valid.
// ---------------------------------------------------------------------------

// Pinned to the imported type so vocabulary drift in the taxonomy resolver fails compile.
const CONFIG_STATUSES = [
  'approved',
  'draft',
  'deprecated',
] as const satisfies readonly ObjectiveConfigStatus[];

const TARGET_METRIC_UNITS = [
  'USD',
  'PCT',
  'BPS',
  'COUNT',
] as const satisfies readonly TargetMetricUnit[];

// The closed TargetMetricFamily set already exists as the overlap-vocabulary keys —
// reuse it rather than minting a second family list.
const TARGET_METRIC_FAMILIES = Object.keys(
  OVERLAP_DIMENSION_KEYS_BY_METRIC_FAMILY,
) as readonly TargetMetricFamily[];

export function validateNeuroTruthDecisionPolicyProjection(value: unknown): string[] {
  const errors: string[] = [];
  if (!isRecord(value)) return ['projection_object_required'];
  if (value.schemaVersion !== NEUROTRUTH_DECISION_POLICY_PROJECTION_SCHEMA_VERSION) {
    errors.push('schemaVersion_invalid');
  }
  requireNonEmptyString(value.projectionId, errors, 'projectionId');
  requireNonEmptyString(value.version, errors, 'version');

  if (!isRecord(value.scope)) {
    errors.push('scope_required');
  } else {
    requireNonEmptyString(value.scope.tenantId, errors, 'scope.tenantId');
    if (
      !('companyId' in value.scope) ||
      !(value.scope.companyId === null || typeof value.scope.companyId === 'string')
    ) {
      errors.push('scope.companyId_invalid');
    }
  }

  validateDirectives(value.directives, errors);
  validateConstraints(value.constraints, errors);
  validateScopes(value.scopes, errors);
  validateCaps(value.caps, errors);
  requireStringArray(value.evidenceRefIds, errors, 'evidenceRefIds');

  // An all-empty projection is exactly the inert pattern this contract exists to kill:
  // approved policy that moves nothing. Fail it closed.
  if (
    Array.isArray(value.directives) &&
    Array.isArray(value.constraints) &&
    Array.isArray(value.scopes) &&
    Array.isArray(value.caps) &&
    value.directives.length === 0 &&
    value.constraints.length === 0 &&
    value.scopes.length === 0 &&
    value.caps.length === 0
  ) {
    errors.push('projection_entries_empty');
  }

  const governanceOk = validateGovernance(value.governance, errors, 'governance');
  if (governanceOk && errors.length === 0) {
    const expected = computeNeuroTruthPolicyProjectionDigest(
      value as unknown as NeuroTruthDecisionPolicyProjection,
    );
    const governance = value.governance as ObjectiveConfigGovernance;
    if (governance.checksum !== expected) {
      errors.push(`governance.checksum_mismatch:expected_${expected}`);
    }
  }
  return errors;
}

function validateDirectives(value: unknown, errors: string[]): void {
  if (!Array.isArray(value)) {
    errors.push('directives_required');
    return;
  }
  value.forEach((directive, index) => {
    const path = `directives[${index}]`;
    if (!isRecord(directive)) {
      errors.push(`${path}_object_required`);
      return;
    }
    requireNonEmptyString(directive.directiveId, errors, `${path}.directiveId`);
    if (
      typeof directive.kind !== 'string' ||
      !(NEUROTRUTH_POLICY_DIRECTIVE_KINDS as readonly string[]).includes(directive.kind)
    ) {
      errors.push(`${path}.kind_invalid`);
    }
    requireNonEmptyString(directive.text, errors, `${path}.text`);
    if (
      !('sourceRefId' in directive) ||
      !(directive.sourceRefId === null || typeof directive.sourceRefId === 'string')
    ) {
      errors.push(`${path}.sourceRefId_invalid`);
    }
    requireStringArray(directive.evidenceRefIds, errors, `${path}.evidenceRefIds`);
  });
}

function validateConstraints(value: unknown, errors: string[]): void {
  if (!Array.isArray(value)) {
    errors.push('constraints_required');
    return;
  }
  value.forEach((constraint, index) => {
    const path = `constraints[${index}]`;
    if (!isRecord(constraint)) {
      errors.push(`${path}_object_required`);
      return;
    }
    if (
      typeof constraint.constraintId !== 'string' ||
      !(OBJECTIVE_CONSTRAINT_IDS as readonly string[]).includes(constraint.constraintId)
    ) {
      errors.push(`${path}.constraintId_invalid`);
    }
    requireNonEmptyString(constraint.sourcePhrase, errors, `${path}.sourcePhrase`);
    if (
      constraint.enforcement !== 'block' &&
      constraint.enforcement !== 'withhold_lever_branch' &&
      constraint.enforcement !== 'annotate'
    ) {
      errors.push(`${path}.enforcement_invalid`);
    }
    requireNonEmptyString(constraint.narrationLabel, errors, `${path}.narrationLabel`);
    requireStringArray(constraint.evidenceRefIds, errors, `${path}.evidenceRefIds`);
    const effect = constraint.selectionPolicyEffect;
    if (!isRecord(effect)) {
      errors.push(`${path}.selectionPolicyEffect_required`);
      return;
    }
    requireStringArray(
      effect.excludedLeverBranches,
      errors,
      `${path}.selectionPolicyEffect.excludedLeverBranches`,
    );
    requireStringArray(
      effect.requiredEvidenceKeys,
      errors,
      `${path}.selectionPolicyEffect.requiredEvidenceKeys`,
    );
    // THE FENCE (issue #4004): 'withhold_lever_branch' with zero excluded branches is the
    // exact inert combination live today — approved prose that withholds nothing. ERROR,
    // so the pattern cannot be re-created through this contract.
    if (
      constraint.enforcement === 'withhold_lever_branch' &&
      Array.isArray(effect.excludedLeverBranches) &&
      effect.excludedLeverBranches.length === 0
    ) {
      errors.push(`${path}.selectionPolicyEffect_empty_for_withhold_lever_branch`);
    }
  });
}

function validateScopes(value: unknown, errors: string[]): void {
  if (!Array.isArray(value)) {
    errors.push('scopes_required');
    return;
  }
  value.forEach((scope, index) => {
    const path = `scopes[${index}]`;
    if (!isRecord(scope)) {
      errors.push(`${path}_object_required`);
      return;
    }
    requireNonEmptyString(scope.scopeId, errors, `${path}.scopeId`);
    const scopeKindValid =
      typeof scope.scopeKind === 'string' &&
      (NEUROTRUTH_POLICY_SCOPE_KINDS as readonly string[]).includes(scope.scopeKind);
    if (!scopeKindValid) {
      errors.push(`${path}.scopeKind_invalid`);
    }
    if (scope.effect !== 'include_only' && scope.effect !== 'exclude') {
      errors.push(`${path}.effect_invalid`);
    }
    if (
      !Array.isArray(scope.values) ||
      scope.values.length === 0 ||
      scope.values.some((item) => typeof item !== 'string')
    ) {
      errors.push(`${path}.values_required`);
    } else if (
      scopeKindValid &&
      scope.scopeKind === 'metric_family' &&
      scope.values.some(
        (item) => !(TARGET_METRIC_FAMILIES as readonly string[]).includes(item as string),
      )
    ) {
      // metric_family values must come from the closed TargetMetricFamily vocabulary.
      errors.push(`${path}.values_invalid`);
    }
    requireStringArray(
      scope.appliesToLeverFamilyIds,
      errors,
      `${path}.appliesToLeverFamilyIds`,
    );
    requireNonEmptyString(scope.sourcePhrase, errors, `${path}.sourcePhrase`);
    requireStringArray(scope.evidenceRefIds, errors, `${path}.evidenceRefIds`);
  });
}

function validateCaps(value: unknown, errors: string[]): void {
  if (!Array.isArray(value)) {
    errors.push('caps_required');
    return;
  }
  value.forEach((cap, index) => {
    const path = `caps[${index}]`;
    if (!isRecord(cap)) {
      errors.push(`${path}_object_required`);
      return;
    }
    requireNonEmptyString(cap.capId, errors, `${path}.capId`);
    if (
      typeof cap.capKind !== 'string' ||
      !(NEUROTRUTH_POLICY_CAP_KINDS as readonly string[]).includes(cap.capKind)
    ) {
      errors.push(`${path}.capKind_invalid`);
    }
    if (typeof cap.value !== 'number' || !Number.isFinite(cap.value) || cap.value < 0) {
      errors.push(`${path}.value_invalid`);
    }
    if (
      typeof cap.unit !== 'string' ||
      !(TARGET_METRIC_UNITS as readonly string[]).includes(cap.unit)
    ) {
      errors.push(`${path}.unit_invalid`);
    }
    // A cap that applies to no lever family caps nothing — inert entries fail closed.
    if (
      !Array.isArray(cap.appliesToLeverFamilyIds) ||
      cap.appliesToLeverFamilyIds.length === 0 ||
      cap.appliesToLeverFamilyIds.some((item) => typeof item !== 'string')
    ) {
      errors.push(`${path}.appliesToLeverFamilyIds_required`);
    }
    requireNonEmptyString(cap.sourcePhrase, errors, `${path}.sourcePhrase`);
    requireStringArray(cap.evidenceRefIds, errors, `${path}.evidenceRefIds`);
  });
}

function validateGovernance(
  governance: unknown,
  errors: string[],
  path: string,
): governance is ObjectiveConfigGovernance {
  const before = errors.length;
  if (!isRecord(governance)) {
    errors.push(`${path}_required`);
    return false;
  }
  if (
    typeof governance.status !== 'string' ||
    !(CONFIG_STATUSES as readonly string[]).includes(governance.status)
  ) {
    errors.push(`${path}.status_invalid`);
  }
  requireNonEmptyString(governance.approvedAt, errors, `${path}.approvedAt`);
  requireNonEmptyString(governance.approvalRefId, errors, `${path}.approvalRefId`);
  requireNonEmptyString(governance.checksum, errors, `${path}.checksum`);
  requireNonEmptyString(governance.effectiveFrom, errors, `${path}.effectiveFrom`);
  if (
    !('effectiveTo' in governance) ||
    !(governance.effectiveTo === null || typeof governance.effectiveTo === 'string')
  ) {
    errors.push(`${path}.effectiveTo_invalid`);
  }
  if (
    !Array.isArray(governance.sourceEvidenceRefs) ||
    governance.sourceEvidenceRefs.some((ref) => typeof ref !== 'string')
  ) {
    errors.push(`${path}.sourceEvidenceRefs_invalid`);
  }
  return errors.length === before;
}

// ---------------------------------------------------------------------------
// Resolution — projection → fully-populated ObjectiveConstraint[]
// ---------------------------------------------------------------------------

// Everything the resolver needs to fail closed. The caller (runner lane) supplies the
// live catalogs; this module never guesses at lever ids — LeverBranchId already has three
// disconnected string catalogs in the repo and this contract does not mint a fourth.
export type NeuroTruthPolicyResolutionContext = Readonly<{
  knownConstraintIds: readonly ObjectiveConstraintId[];
  knownLeverBranchIds: readonly LeverBranchId[];
  knownLeverFamilyIds: readonly string[];
}>;

// Pure + deterministic: same projection + context ⇒ same output, in projection order.
// Fail-closed (mirrors the taxonomy resolver): an invalid projection, an unapproved
// governance status, an unknown constraint id, or an unknown lever branch/family THROWS —
// nothing is silently dropped. Output is assignable to the EXISTING ObjectiveConstraint
// contract (the return type is that contract — reuse, never fork), with NON-empty
// selectionPolicyEffect wherever the projection declares effects (guaranteed by the
// withhold fence in validation plus verbatim effect copy here).
export function resolveProjectionToObjectiveConstraints(
  projection: NeuroTruthDecisionPolicyProjection,
  context: NeuroTruthPolicyResolutionContext,
): readonly ObjectiveConstraint[] {
  const errors = validateNeuroTruthDecisionPolicyProjection(projection);
  if (errors.length > 0) {
    throw new Error(`neurotruth_policy_projection_invalid:${errors.join('|')}`);
  }
  if (projection.governance.status !== 'approved') {
    throw new Error(
      `neurotruth_policy_projection_not_approved:${projection.governance.status}`,
    );
  }
  for (const constraint of projection.constraints) {
    if (!context.knownConstraintIds.includes(constraint.constraintId)) {
      throw new Error(`neurotruth_policy_unknown_constraint:${constraint.constraintId}`);
    }
    for (const branch of constraint.selectionPolicyEffect.excludedLeverBranches) {
      if (!context.knownLeverBranchIds.includes(branch)) {
        throw new Error(`neurotruth_policy_unknown_lever_branch:${branch}`);
      }
    }
  }
  for (const cap of projection.caps) {
    for (const familyId of cap.appliesToLeverFamilyIds) {
      if (!context.knownLeverFamilyIds.includes(familyId)) {
        throw new Error(`neurotruth_policy_unknown_lever_family:${familyId}`);
      }
    }
  }
  for (const scope of projection.scopes) {
    for (const familyId of scope.appliesToLeverFamilyIds) {
      if (!context.knownLeverFamilyIds.includes(familyId)) {
        throw new Error(`neurotruth_policy_unknown_lever_family:${familyId}`);
      }
    }
    if (scope.scopeKind === 'lever_branch') {
      for (const branch of scope.values) {
        if (!context.knownLeverBranchIds.includes(branch)) {
          throw new Error(`neurotruth_policy_unknown_lever_branch:${branch}`);
        }
      }
    }
  }
  return projection.constraints.map((constraint) => ({
    constraintId: constraint.constraintId,
    sourcePhrase: constraint.sourcePhrase,
    // Enforcement points evaluate satisfaction later; at seed time the honest status is
    // 'unknown' — never a presumed 'satisfied'.
    status: 'unknown',
    enforcement: constraint.enforcement,
    selectionPolicyEffect: {
      excludedLeverBranches: [...constraint.selectionPolicyEffect.excludedLeverBranches],
      requiredEvidenceKeys: [...constraint.selectionPolicyEffect.requiredEvidenceKeys],
    },
    narrationLabel: constraint.narrationLabel,
    evidenceRefIds: [...constraint.evidenceRefIds],
  }));
}

// ---------------------------------------------------------------------------
// Local validation helpers (kept here so the module stays dependency-free).
// ---------------------------------------------------------------------------

function requireNonEmptyString(value: unknown, errors: string[], path: string): void {
  if (typeof value !== 'string' || value.trim().length === 0) {
    errors.push(`${path}_required`);
  }
}

function requireStringArray(value: unknown, errors: string[], path: string): void {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) {
    errors.push(`${path}_invalid`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
